import * as React from 'react'
import { FunctionComponent, useState } from 'react'
import { Grid, Typography } from '@mui/material'
import { mergeDeepRight, valuesIn } from 'ramda'

import {
  Errors,
  FieldNames,
  FormContext,
  FormValidators,
  SubmitResponse,
  Values,
} from '@ecm/data/Form'
import Button from '@ecm/ui/element/Button'
import Color from '@ecm/ui/element/Color'
import { Validator } from '@ecm/ui/element/Form/Validation'

type FormProps = {
  buttonWidth?: string
  children?: React.ReactNode
  className?: string
  green?: boolean
  name: string
  onSubmit: (values: Values) => Promise<SubmitResponse>
  submitText?: string
  validationMessage?: string
}
// This handles validation through context with its child inputs
// on submit it will validate and show errors without any work from parent components

// the Parent component must pass a function to handle submit after the form is validated
// the submit handler can pass back a message to display under the form or run a function(onComplete), or both
// the intent is to use this for all forms to keep validation, error, and message handling consistent

export const FormContextFull = React.createContext<FormContext>({
  addValidator: (validator: { [k in FieldNames]?: Validator }) => {
    if (validator) {
      return
    }
  },
  errors: {},
  setErrors: (e: Errors) => {
    if (e) {
      return
    }
  },
  setValues: (v: Values) => {
    if (v) {
      return
    }
  },
})

export const Form: FunctionComponent<FormProps> = ({
  buttonWidth,
  className,
  children,
  green,
  name,
  onSubmit,
  submitText = 'Continue',
  validationMessage,
}: FormProps) => {
  const [validators, setValidators] = useState<FormValidators>({})
  const [submitResponse, setSubmitResponse] = useState<SubmitResponse>({})
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [values, setValues] = useState<Values>({})
  const [errors, setErrors] = useState<Errors>({})

  const initialFormContext: FormContext = {
    addValidator: (v: { [k in FieldNames]?: Validator }) => {
      setValidators(val => {
        return mergeDeepRight(val, v)
      })
    },
    errors: errors,
    setErrors: (e: Errors) => setErrors(errors => mergeDeepRight(errors, e)),
    setValues: (v: Values) => setValues(values => mergeDeepRight(values, v)),
  }

  const validateInputs = (fieldValues: Values, fieldValidators: FormValidators): Errors => {
    const errors = Object.keys(fieldValidators).reduce((acc: Errors, f) => {
      const validateFunction = fieldValidators[f as FieldNames]
      const value = fieldValues[f as FieldNames]
      if (validateFunction) {
        acc[f as FieldNames] = validateFunction(value || '')
      }
      return acc
    }, {})
    return errors
  }

  const checkForErrors = (errorsOnSubmit: Errors): boolean => {
    const errorValues: string[] = valuesIn(errorsOnSubmit)
    return !errorValues.some(v => v.length)
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()

    const errors = validateInputs(values, validators)

    setErrors(errors)

    if (checkForErrors(errors)) {
      setSubmitting(true)
      const submitSuccess = await onSubmit(values)
      setSubmitResponse(submitSuccess)
      if (submitSuccess.onComplete) {
        submitSuccess.onComplete()
        setSubmitting(false)
      }
    } else {
      const errorSubmitResponse: SubmitResponse = {
        message: validationMessage,
        status: 'error',
      }
      setSubmitResponse(errorSubmitResponse)
      setSubmitting(false)
    }
  }

  return (
    <FormContextFull.Provider value={initialFormContext}>
      <form name={name} className={className} onSubmit={handleSubmit} noValidate={true}>
        <Grid>
          {children}
          {
            <Typography
              variant="p3"
              margin="0 0 1em 0"
              color={submitResponse.status === 'error' ? Color.love.normal : Color.lucky.normal}
              visibility={
                submitResponse.status === 'error' || submitResponse.status === 'success'
                  ? 'visible'
                  : 'hidden'
              }
            >
              {submitResponse.message}
            </Typography>
          }

          <Grid container justifyContent="flex-end">
            <Button
              width={buttonWidth}
              submitting={submitting}
              primary={!green}
              green={green}
              type="submit"
            >
              {submitText}
            </Button>
          </Grid>
        </Grid>
      </form>
    </FormContextFull.Provider>
  )
}
